import BlankCellViewer from '@/components/dataGrid/components/renderers/cell/view/blankCellViewer.vue';
import AddColumnHeader from '@/components/dataGrid/components/renderers/headers/AddColumnHeader.vue';
import Helpers from '@/helpers/index.js';
import { default as ProjectHelpers, default as ProjectsHelpers } from '@/helpers/projects.helpers.ts';
import { API } from '@/services';
import { CREATE_ENTITY } from '@/services/account/mutations/account/index.js';
import { CREATE_PROJECT_CREATOR_DOCUMENT_MUTATION, UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION } from '@/services/project/mutations/document/index.js';
import { GET_PROJECT_CREATOR_DOCUMENTS_QUERY, GET_PROJECT_ENTITY_MAPPING_QUERY } from '@/services/project/queries/index.js';
import { UPDATE_PROJECT_VIEWS_MUTATION } from '@/services/project/mutations/project/index.js';
import { defineStore } from 'pinia';
import { useProjectStore } from './index.store.ts';

const DOCUMENT_CURRENCY_FIELD = 'Currency';
const DOCUMENT_CURRENCY_COL_ID = 'currency_custom_property.display_value';
const DOCUMENT_INVOICE_AMOUNT_COL_ID = 'invoice_amount.display_value';
const DOCUMENT_LEGACY_COLUMN_ID_MAP = {
  'invoiceamount_custom_property.display_value': DOCUMENT_INVOICE_AMOUNT_COL_ID,
  'finalrate_custom_property.display_value': 'initial_quote.display_value',
  'remainingbalance_custom_property.display_value': 'remaining_balance.display_value',
  'paymentmethod_custom_property.display_value': 'payment_method.display_value',
};
const DOCUMENT_CURRENCY_OPTIONS = [
  'USD::US Dollar (USD)',
  'EUR::Euro (EUR)',
  'JPY::Japanese Yen (JPY)',
  'GBP::British Pound Sterling (GBP)',
  'CNY::Chinese Yuan/Renminbi (CNY/CNH)',
  'AUD::Australian Dollar (AUD)',
  'CAD::Canadian Dollar (CAD)',
  'CHF::Swiss Franc (CHF)',
  'HKD::Hong Kong Dollar (HKD)',
  'SGD::Singapore Dollar (SGD)',
  'NZD::New Zealand Dollar (NZD)',
  'SEK::Swedish Krona (SEK)',
  'NOK::Norwegian Krone (NOK)',
  'KRW::South Korean Won (KRW)',
  'INR::Indian Rupee (INR)',
];
const DOCUMENT_CURRENCY_DROPDOWN_OPTIONS = DOCUMENT_CURRENCY_OPTIONS.map((option) => ProjectsHelpers.formatEntityMappingOption(option));

const hasEntityMapping = (mappings = [], fieldName) => {
  return mappings.some((mapping) => mapping.field?.toLowerCase() === fieldName.toLowerCase());
};

const normalizeDocumentColumnId = (colId) => DOCUMENT_LEGACY_COLUMN_ID_MAP[colId] ?? colId;

const normalizeDocumentOrderedColIds = (orderedColIds = []) => {
  const seenColIds = new Set();

  return orderedColIds
    .map((colId) => normalizeDocumentColumnId(colId))
    .filter((colId) => {
      if (!colId || seenColIds.has(colId)) {
        return false;
      }

      seenColIds.add(colId);
      return true;
    });
};

const insertCurrencyAfterInvoiceAmount = (orderedColIds = []) => {
  const filteredColIds = normalizeDocumentOrderedColIds(orderedColIds).filter((colId) => colId !== DOCUMENT_CURRENCY_COL_ID);
  const invoiceAmountIndex = filteredColIds.indexOf(DOCUMENT_INVOICE_AMOUNT_COL_ID);

  if (invoiceAmountIndex === -1) {
    return [...filteredColIds, DOCUMENT_CURRENCY_COL_ID];
  }

  return [...filteredColIds.slice(0, invoiceAmountIndex + 1), DOCUMENT_CURRENCY_COL_ID, ...filteredColIds.slice(invoiceAmountIndex + 1)];
};

const normalizeDocumentView = (view) => {
  if (!view) {
    return view;
  }

  let column_state = view.column_state;
  let isCurrencyMissingFromView = false;

  if (Array.isArray(column_state)) {
    const normalizedColumnState = column_state
      .map((state) => ({
        ...state,
        colId: normalizeDocumentColumnId(state.colId),
      }))
      .filter((state, index, states) => state.colId && states.findIndex((item) => item.colId === state.colId) === index);

    isCurrencyMissingFromView = !normalizedColumnState.some((state) => state.colId === DOCUMENT_CURRENCY_COL_ID);

    if (isCurrencyMissingFromView && normalizedColumnState.length > 0) {
      const currencyState = normalizedColumnState.find((state) => state.colId === DOCUMENT_CURRENCY_COL_ID) ?? { colId: DOCUMENT_CURRENCY_COL_ID };
      const filteredState = normalizedColumnState.filter((state) => state.colId !== DOCUMENT_CURRENCY_COL_ID);
      const invoiceAmountIndex = filteredState.findIndex((state) => state.colId === DOCUMENT_INVOICE_AMOUNT_COL_ID);
      column_state =
        invoiceAmountIndex === -1
          ? [...filteredState, currencyState]
          : [...filteredState.slice(0, invoiceAmountIndex + 1), currencyState, ...filteredState.slice(invoiceAmountIndex + 1)];
    } else {
      column_state = normalizedColumnState;
    }
  } else if (column_state && typeof column_state === 'object') {
    const orderedColIds = normalizeDocumentOrderedColIds(column_state.columnOrder?.orderedColIds ?? []);
    isCurrencyMissingFromView = !orderedColIds.includes(DOCUMENT_CURRENCY_COL_ID);

    if (isCurrencyMissingFromView) {
      column_state = {
        ...column_state,
        columnOrder: {
          ...(column_state.columnOrder ?? {}),
          orderedColIds: insertCurrencyAfterInvoiceAmount(orderedColIds),
        },
      };
    } else {
      column_state = {
        ...column_state,
        columnOrder: {
          ...(column_state.columnOrder ?? {}),
          orderedColIds,
        },
      };
    }
  }

  const hidden_fields =
    isCurrencyMissingFromView && Array.isArray(view.hidden_fields)
      ? view.hidden_fields.filter((field) => field.headerName !== DOCUMENT_CURRENCY_FIELD)
      : view.hidden_fields;

  return {
    ...view,
    hidden_fields,
    column_state,
  };
};

export const useProjectDocumentsStore = defineStore({
  id: 'projectDocuments',
  state: () => ({
    showGrid: true,
    documents: [],
    creators: [],
    data: [],
    isLoadingDocuments: false,
    activeDocuments: false,
    views: [],
    filterOptions: [],
    activeView: {
      name: 'Important Docs by Creator',
      base: 'documents',
      canEdit: false,
      shared: false,
      display_mode: 'grid',
      grouping: [],
      sort: [],
      filter: null,
      hidden_fields: [{ headerName: 'Total Est. Impressions' }],
      column_state: [],
      custom_properties: [{ key: 'Last Used', value: 'true' }],
    },
    entityMappings: {
      creators: [],
      documents: [],
    },
    projectHelpers: ProjectsHelpers,
  }),
  getters: {
    mergedEntities(state) {
      return [...state.entityMappings.creators, ...state.entityMappings.documents];
    },
    columns(state) {
      // generate custom columns based on the project's (documents) entity mappings'
      const customColumnSettings = {
        entityOrigin: 'project_documents',
        isCreatorsEntity: state.entityMappings.creators.length > 0,
      };

      const customColumns = this.mergedEntities.map((mapping) => ProjectsHelpers.formatCustomColumn(mapping, customColumnSettings, useProjectStore().isCreatorDetailsViewActive));
      const currencyColumn = customColumns.find((column) => column.headerName === DOCUMENT_CURRENCY_FIELD && column.context?.entityName === 'project_documents');
      const otherCustomColumns = customColumns.filter((column) => column !== currencyColumn);

      const addColumn = {
        headerName: 'addColumn',
        headerComponent: AddColumnHeader,
        cellRenderer: BlankCellViewer,
        field: 'addColumn',
        width: 150,
        sort: false,
        filter: false,
        suppressHeaderMenuButton: true, // Disable any context menu on this column
        suppressMovable: true, // Prevent this column from being moved
        suppressColumnsToolPanel: true, // Hide the column in the columns panel
        lockPosition: 'right',
        suppressSizeToFit: true,
        suppressHeaderContextMenu: true,
      };

      let data = [
        {
          field: 'label.display_value',
          headerName: 'File Name',
          headerClass: 'header-string',
          sortable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
          },
          editable: true,
          cellEditorParams: {
            key: 'label',
          },
          context: {},
        },
        {
          field: 'type.display_value',
          headerName: 'Document Type',
          headerClass: 'header-dropdown',
          sortable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getDocumentTypeOptions().map((option) => option?.value),
          },
          editable: true,
          cellEditorParams: {
            key: 'type',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            dropdownSelections: ProjectHelpers.getDocumentTypeOptions(),
          },
        },
        {
          field: 'file.display_value',
          headerName: 'File',
          headerClass: 'header-file',
          sortable: true,
          filter: true,
          editable: false,
          filterParams: {
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'file',
          },
          context: {
            turnOffEndpoint: true,
            renderViewType: 'file',
          },
        },
        /*{
          field: 'username.display_value',
          headerName: 'Name',
          headerClass: 'header-user',
          sortable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
          },
          filtersParams: {
            keyCreator: (params) => params.value,
            valueFormatter: (params) => params.value ?? 0,
          },
          editable: false,
          suppressPaste: true,
          suppressFillHandle: true,
          cellEditorParams: {
            key: 'user',
          },
          context: {
            turnOffEndpoint: true,
            renderViewType: 'user',
            renderEditType: 'user',
          },
        },*/
        {
          field: 'username.display_value',
          headerName: 'Name',
          headerClass: 'header-user',
          sortable: true,
          editable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
          },
          // filter: true,
          // filterParams: {
          //   defaultToNothingSelected: true,
          //   suppressMultiSelect: true
          //   // suppressSetFilter: true // Disables multi-select on filter
          //   // defaultOption: 'equals', // Default condition
          //   // filterOptions: ['equals'], // Lock to "Equals" only
          //   // alwaysShowBothConditions: false // Hide additional condition dropdown
          // },
          cellEditorParams: {
            key: 'creator',
          },
          context: {
            renderViewType: 'user',
            renderEditType: 'user',
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row

              params.event.preventDefault();
              return true;
            }
          },
        },
        {
          field: 'paypal_email_custom_property.display_value',
          headerName: 'Paypal Email',
          headerClass: 'header-string',
          sortable: true,
          editable: false,
          suppressPaste: true,
          suppressFillHandle: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'paypal_email_custom_property',
          },
          context: {
            turnOffEndpoint: true,
            renderViewType: 'string',
          },
        },
        {
          field: 'currency_custom_property.display_value',
          headerName: 'Currency',
          headerClass: 'header-single-select-freeform',
          sortable: true,
          filter: true,
          editable: true,
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: DOCUMENT_CURRENCY_DROPDOWN_OPTIONS.map((option) => option.value),
          },
          cellEditorParams: {
            key: 'currency_custom_property',
          },
          context: {
            renderViewType: 'string',
            renderEditType: 'single-select-freeform',
            multiSelect: false,
            dropdownSelections: DOCUMENT_CURRENCY_DROPDOWN_OPTIONS,
            payloadTransformer: (payload) => {
              return payload !== null && payload !== undefined ? String(payload) : '';
            },
          },
        },
        {
          field: 'initial_quote.display_value',
          headerName: 'Final Rate',
          headerClass: 'header-currency',
          cellDataType: 'number',
          sortable: true,
          filter: false,
          editable: true,
          singleClickEdit: true,
          cellRenderer: ({ data }) => {
            const val = data?.initial_quote?.display_value;
            if (val !== '' && val !== null && val !== undefined) {
              const numVal = Number(val);
              if (!isNaN(numVal)) {
                return `$${Helpers.formatNumberWithCommas(numVal)}`;
              }
            }
            return '';
          },
          keyCreator: (params) => (params.value ? `$${Helpers.formatNumberWithCommas(Number(params.value))}` : '$0'),
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'initial_quote',
            precision: 2,
            step: 0.01,
            showStepperButtons: true,
          },
          context: {
            turnOffEndpoint: true,
            filterType: 'currency',
            renderViewType: 'custom',
            renderEditType: 'currency',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'invoice_amount.display_value',
          cellRenderer: ({ data }) => {
            const val = data?.invoice_amount?.display_value;
            if (val !== '' && val !== null && val !== undefined) {
              const numVal = Number(val);
              if (!isNaN(numVal)) {
                return `$${Helpers.formatNumberWithCommas(numVal)}`;
              }
            }
            return '';
          },
          keyCreator: (params) => (params.value ? `$${Helpers.formatNumberWithCommas(Number(params.value))}` : '$0'),
          headerName: 'Invoice Amount',
          headerClass: 'header-currency',
          singleClickEdit: true,
          cellDataType: 'number',
          sortable: true,
          editable: true,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'invoice_amount',
            precision: 2,
            step: 0.01,
            showStepperButtons: true,
          },
          context: {
            turnOffEndpoint: false,
            filterType: 'currency',
            renderViewType: 'custom',
            payloadTransformer: (payload) => {
              return String(payload);
            },
          },
        },
        {
          field: 'remaining_balance.display_value',
          valueGetter: (params) => {
            // Calculate remaining balance dynamically based on current data
            const initialQuoteRaw = params.data?.initial_quote?.display_value ?? 0;
            const initialQuote = (() => {
              const parsed = parseFloat(initialQuoteRaw);
              return !isNaN(parsed) ? parsed : 0;
            })();
            
            // Get all rows for the same creator to calculate cumulative remaining balance
            const creatorId = params.data?.creator_account_id;
            if (!creatorId) {
              // Fallback to stored value if creator ID not available
              const val = params.data?.remaining_balance?.display_value;
              if (val !== '' && val !== null && val !== undefined && !isNaN(val)) {
                return Math.round(Number(val) * 100) / 100;
              }
              return null;
            }
            
            // If API is available, calculate dynamically
            if (params.api) {
              // Get all rows for this creator
              let allRows = [];
              params.api.forEachNode((node) => {
                if (node.data?.creator_account_id === creatorId) {
                  allRows.push({ data: node.data, index: node.rowIndex });
                }
              });
              
              // Sort by row index to maintain order
              allRows.sort((a, b) => a.index - b.index);
              
              // Calculate cumulative remaining balance
              let remaining = initialQuote;
              for (const { data: row } of allRows) {
                const rowInvoiceAmountRaw = row?.invoice_amount?.display_value ?? 0;
                const rowInvoiceAmount = (() => {
                  const parsed = parseFloat(rowInvoiceAmountRaw);
                  return !isNaN(parsed) ? parsed : 0;
                })();
                remaining -= rowInvoiceAmount;
                
                // If this is the current row, return the calculated balance
                if (row === params.data) {
                  // Round to 2 decimal places
                  return Math.round(remaining * 100) / 100;
                }
              }
            }
            
            // Fallback to stored value
            const val = params.data?.remaining_balance?.display_value;
            if (val !== '' && val !== null && val !== undefined && !isNaN(val)) {
              return Math.round(Number(val) * 100) / 100;
            }
            return null;
          },
          cellRenderer: ({ data, value, api }) => {
            // Use value from valueGetter if available, otherwise fall back to stored value
            let val = value;
            if (val === null || val === undefined) {
              val = data?.remaining_balance?.display_value;
            }
            
            if (val !== '' && val !== null && val !== undefined && !isNaN(val)) {
              // Round to 2 decimal places to avoid floating point precision issues
              const rounded = Math.round(Number(val) * 100) / 100;
              return `$${Helpers.formatNumberWithCommas(rounded)}`;
            }
            return '';
          },
          headerName: 'Remaining Balance',
          headerClass: 'header-formula',
          sortable: true,
          editable: false,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'remaining_balance',
          },
          context: {
            turnOffEndpoint: true,
            filterType: 'currency',
            renderViewType: 'custom',
          },
        },
        {
          field: 'live_date.display_value',
          headerName: 'Live Date',
          headerClass: 'header-date',
          sortable: true,
          editable: false,
          suppressPaste: true,
          suppressFillHandle: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'live_date',
          },
          context: {
            turnOffEndpoint: true,
            renderViewType: 'date',
          },
        },
        {
          field: 'actual_views_impressions.display_value',
          cellRenderer: ({ data }) => {
            const val = data?.actual_views_impressions?.display_value;
            if (val !== '' && val !== null && val !== undefined) {
              const numVal = Number(val);
              if (!isNaN(numVal)) {
                return Helpers.formatNumberWithCommas(numVal);
              }
            }
            return '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          headerName: '# of Views/Impressions',
          headerClass: 'header-number',
          cellDataType: 'number',
          sortable: true,
          editable: false,
          suppressPaste: true,
          suppressFillHandle: true,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'actual_views_impressions',
          },
          context: {
            turnOffEndpoint: true,
            filterType: 'number',
            renderViewType: 'string',
          },
        },
        {
          field: 'actual_engagements.display_value',
          cellRenderer: ({ data }) => {
            const val = data?.actual_engagements?.display_value;
            if (val !== '' && val !== null && val !== undefined) {
              const numVal = Number(val);
              if (!isNaN(numVal)) {
                return Helpers.formatNumberWithCommas(numVal);
              }
            }
            return '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          headerName: '# of Engagements',
          headerClass: 'header-number',
          cellDataType: 'number',
          sortable: true,
          editable: false,
          suppressPaste: true,
          suppressFillHandle: true,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'actual_engagements',
          },
          context: {
            turnOffEndpoint: true,
            filterType: 'number',
            renderViewType: 'string',
          },
        },
        {
          field: 'total_estimated_impressions',
          headerClass: 'header-formula',
          headerName: 'Total Est. Impressions',
          cellRenderer: (params) => {
            // add instagram, tiktok, and youtube impressions
            const impressions =
              Number(params.data?.creator_instagram_impressions?.display_value) + Number(params.data?.creator_tiktok_impressions?.display_value) + Number(params.data?.creator_youtube_impressions?.display_value);
            return impressions ? Helpers.formatNumberWithCommas(Number(impressions)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
          },
          cellEditorParams: {
            key: 'total_estimated_impressions',
          },
          context: {
            renderViewType: 'custom',
          },
        },
        {
          field: 'pay_approved_date.display_value',
          headerName: 'Pay Approved',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.pay_approved_date.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'pay_approved_date',
          },
          context: {
            renderViewType: 'date',
            renderEditType: 'date',
            payloadTransformer: (payload) => {
              if (!payload) return null;
              if (payload instanceof Date) {
                return payload.toISOString();
              }
              return payload;
            },
          },
        },
        {
          field: 'approver_name.display_value',
          headerName: 'Approver Name',
          headerClass: 'header-string',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          filter: true,
          cellEditorParams: {
            key: 'approver_name',
          },
          context: {
            renderViewType: 'string',
            renderEditType: 'string',
          },
        },
        {
          field: 'submitteddate_custom_property.display_value',
          headerName: 'Submitted Date',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.pay_approved_date.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'submitteddate_custom_property',
          },
          context: {
            renderViewType: 'date',
            renderEditType: 'date',
          },
        },
        {
          field: 'pay_status.display_value',
          headerName: 'Pay Status',
          headerClass: 'header-dropdown',
          sortable: true,
          filter: true,
          cellRenderer: ({ data }) => {
            // Display the already-mapped label from display_value
            return data?.pay_status?.display_value || 'Pending Approval';
          },
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ProjectHelpers.getPaymentStatusOptions().map((option) => option?.label),
          },
          filterValueGetter: (params) => {
            // Return the display value which is already mapped to the label
            return params.data?.pay_status?.display_value || 'Pending Approval';
          },
          editable: true,
          cellEditorParams: {
            key: 'pay_status',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: false,
            dropdownSelections: ProjectHelpers.getPaymentStatusOptions(),
            payloadTransformer: (payload) => {
              // When saving, send the GraphQL string value (not numeric DB value)
              // The dropdown already provides the correct GraphQL value
              if (payload === 'Pending Approval' || payload === '' || payload === null || payload === undefined) {
                return '';
              }
              // If somehow we receive a numeric DB value, map it to GraphQL string value
              if (payload === '0') {
                return 'Ready_to_Pay';
              } else if (payload === '1') {
                return 'Processing';
              } else if (payload === '2') {
                return 'Paid';
              }
              // Otherwise send as-is (should be 'Ready_to_Pay', 'Processing', 'Paid', or '')
              return payload;
            },
          },
        },
        {
          field: 'paid_date.display_value',
          headerName: 'Paid Date',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.paid_date.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'paid_date',
          },
          context: {
            renderViewType: 'date',
            renderEditType: 'date',
            payloadTransformer: (payload) => {
              if (!payload) return null;
              if (payload instanceof Date) {
                return payload.toISOString();
              }
              return payload;
            },
          },
        },
        {
          field: 'payment_method.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Pay by',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectHelpers.getPayByOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'payment_method',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: false,
            dropdownSelections: ProjectHelpers.getPayByOptions(),
          },
        }
      ];

      if (useProjectStore().isCreatorDetailsViewActive) {
        // only add the add column header if we have access to the creator details view as well.
        data.push(...otherCustomColumns, addColumn);
      }

      const builtInCurrencyColumnIndex = data.findIndex((column) => column.field === DOCUMENT_CURRENCY_COL_ID);
      const invoiceAmountColumnIndex = data.findIndex((column) => column.field === DOCUMENT_INVOICE_AMOUNT_COL_ID);

      if (builtInCurrencyColumnIndex !== -1 && invoiceAmountColumnIndex !== -1 && builtInCurrencyColumnIndex !== invoiceAmountColumnIndex + 1) {
        const [builtInCurrencyColumn] = data.splice(builtInCurrencyColumnIndex, 1);
        const nextInvoiceAmountColumnIndex = data.findIndex((column) => column.field === DOCUMENT_INVOICE_AMOUNT_COL_ID);
        data.splice(nextInvoiceAmountColumnIndex + 1, 0, builtInCurrencyColumn);
      }

      // don't show custom columns that are connected to the creator details view. Protects these from accidental deletion to the creator details view.
      return data.filter((column) => column.context?.entityName !== 'project_creator');
    },
  },
  actions: {
    async initProjectDocuments(isLoading = true) {
      this.isLoadingDocuments = isLoading;
      const [documentsEntities, creatorsEntities, documents] = await Promise.all([
        API.sendQuery('/graphql/project', GET_PROJECT_ENTITY_MAPPING_QUERY, { project_id: Number(useProjectStore().currentProjectID), entity_name: ['project_documents'] }),
        API.sendQuery('/graphql/project', GET_PROJECT_ENTITY_MAPPING_QUERY, { project_id: Number(useProjectStore().currentProjectID), entity_name: ['project_creator'] }),
        API.sendQuery('/graphql/project', GET_PROJECT_CREATOR_DOCUMENTS_QUERY, {
          project_id: Number(useProjectStore().currentProjectID),
        }),
      ]);

      if (documentsEntities.data.data.entityMappings) {
        this.entityMappings.documents = documentsEntities.data.data.entityMappings;
      }

      if (creatorsEntities.data.data.entityMappings) {
        this.entityMappings.creators = creatorsEntities.data.data.entityMappings;
      }

      await this.ensureDocumentCurrencySetup();

      if (documents.data.data.creatorDocuments) {
        this.setDocuments(documents.data.data.creatorDocuments);
      }

      this.isLoadingDocuments = false;
    },

    setFilterOptions(column, options) {
      this.filterOptions.push({
        field: column.field,
        type: column.type,
        filterType: column?.context?.filterType,
        columnHeaderName: column.headerName,
        options: options,
        key: column?.cellEditorParams?.key,
      });
    },

    setFilteredSortedDocuments(data) {
      this.filteredSortedCreators = data;
    },

    setFilteredActiveDocuments(data) {
      this.filteredActiveCreators = data;
    },

    setViews(views) {
      if (views && views.length > 0) {
        this.activeDocuments = true;
        // check if the views array contain a view name that is "Creator Documents" and make that the first one in the array
        const creatorDocumentsView = views.find((view) => view.name === 'Important Docs by Creator');
        if (creatorDocumentsView) {
          views = views.filter((view) => view.name !== 'Important Docs by Creator');
          views.unshift(creatorDocumentsView);
        }
        this.views = views.map((view) => normalizeDocumentView(view));
      }
    },

    async setViewShared(projectViewID, shared = true) {
      let res = await API.sendQuery('/graphql/project', UPDATE_PROJECT_VIEWS_MUTATION, { id: projectViewID, shared: shared });
      if (res.data.data.updateView?.id) {
        this.activeView.shared = res.data.data.updateView.shared;
      }
    },

    async ensureDocumentCurrencySetup() {
      if (hasEntityMapping(this.entityMappings.documents, DOCUMENT_CURRENCY_FIELD)) {
        return;
      }

      const res = await API.sendQuery('/graphql/account', CREATE_ENTITY, {
        account_id: Number(useProjectStore().activeAccount.id),
        project_id: Number(useProjectStore().currentProjectID),
        field_name: DOCUMENT_CURRENCY_FIELD,
        entity_name: 'project_documents',
        field_type: 'single-select-freeform',
        options: DOCUMENT_CURRENCY_OPTIONS,
      });

      const newEntity = res.data?.data?.createEntity;
      if (newEntity && !hasEntityMapping(this.entityMappings.documents, DOCUMENT_CURRENCY_FIELD)) {
        this.entityMappings.documents.push(newEntity);
      }
    },

    setActiveView(view) {
      if (view) {
        this.activeView = view;
      }
    },

    setCreators(creators) {
      if (creators && creators.length > 0) {
        this.creators = creators;
      }
    },

    setDocuments(documents) {
      if (documents && documents.length > 0) {
        this.activeDocuments = true;
        this.data = this.generateDocumentsGridData(documents, this.mergedEntities);
      } else {
        this.data = ProjectsHelpers.generateEmptyGridData('documents', 5, 0);
      }
    },

    setEntityMappings(entityMappings) {
      this.entityMappings = entityMappings;
    },

    generateDocumentsGridData(data = [], entityMappings = []) {
      if (data.length > 0) {
        const creatorRemaining = {};

        return data.map((document, index) => {
          const creatorId = document?.project_creator_id;
          const initialQuoteRaw = document?.project_creator?.contract?.contract_value ?? 0;
          const initialQuote = (() => {
            const parsed = parseFloat(initialQuoteRaw);
            return !isNaN(parsed) ? parsed : 0;
          })();
          
          const invoiceAmountRaw = document?.custom_properties?.find(p => p.key === 'invoice_amount')?.value?.[0] ?? 0;
          const invoiceAmount = (() => {
            const parsed = parseFloat(invoiceAmountRaw);
            return !isNaN(parsed) ? parsed : 0;
          })();

          // initialize per creator
          if (!creatorRemaining[creatorId]) {
            creatorRemaining[creatorId] = initialQuote || 0;
          }

          // subtract invoice amount from remaining for next row
          creatorRemaining[creatorId] -= invoiceAmount;

          // Round to 2 decimal places to avoid floating point precision issues
          const remainingBalance = Math.round(creatorRemaining[creatorId] * 100) / 100;

          const customProperties = document?.custom_properties;
          const creatorCustomProperties = document?.project_creator?.custom_properties;
          const customPropertiesFields = {};
          const customEntityMappingsFields = {};

          // just add the creator custom properties to the customPropertiesFields object (if they exist)
          // none editable for now
          if (creatorCustomProperties && creatorCustomProperties.length > 0) {
            creatorCustomProperties.map((customProperty) => {
              const key = customProperty.key.replaceAll(' ', '').toLowerCase() + '_custom_property';
              return (customPropertiesFields[key] = {
                display_value: customProperty.value.length > 1 ? customProperty.value : customProperty.value[0],
              });
            });
          }

          if (customProperties && customProperties.length > 0) {
            customProperties.map((customProperty) => {
              const key = customProperty.key.replaceAll(' ', '').toLowerCase() + '_custom_property';
              let displayValue = customProperty.value.length > 1 ? customProperty.value : customProperty.value[0];

              if (customProperty.value[0] === 'true') {
                displayValue = true;
              }

              if (customProperty.value[0] === 'false') {
                displayValue = false;
              }

              return (customPropertiesFields[key] = {
                display_value: displayValue,
                endpoint: '/graphql/project',
                query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
                key: key,
                originalKey: customProperty.key,
                custom_property: true,
                args: {
                  id: document?.id ? Number(document.id) : null,
                },
              });
            });
          }

          // check for entity mappings
          entityMappings.map((mapping) => {
            const customEntityKey = `${mapping.field.replaceAll(' ', '').toLowerCase()}_custom_property`;

            if (!customPropertiesFields[customEntityKey]) {
              return (customEntityMappingsFields[customEntityKey] = {
                display_value: mapping.entity === 'project_documents' ? document?.custom_properties[customEntityKey] : '',
                endpoint: '/graphql/project',
                query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
                key: customEntityKey,
                originalKey: mapping.field,
                custom_property: true,
                args: {
                  id: document?.id ? Number(document.id) : null,
                },
              });
            }
          });

          const invoice_amount = document?.custom_properties?.find(p => p.key === 'invoice_amount')?.value?.[0];
          const pay_approved_date = document?.custom_properties?.find(p => p.key === 'pay_approved_date')?.value?.[0];
          const approver_name = document?.custom_properties?.find(p => p.key === 'approver_name')?.value?.[0];
          const pay_status = document?.custom_properties?.find(p => p.key === 'pay_status')?.value?.[0];
          const paid_date = document?.custom_properties?.find(p => p.key === 'paid_date')?.value?.[0];
          const payment_method = document?.custom_properties?.find(p => p.key === 'payment_method')?.value?.[0];
          const currency = document?.custom_properties?.find(p => p.key === 'Currency')?.value?.[0] ?? document?.custom_properties?.find(p => p.key === 'currency')?.value?.[0] ?? 'USD';
          const payment_method_src = document?.project_creator?.account_creator?.custom_properties?.find(p => p.key === 'payment_method')?.value?.[0];
          const paypal_email = document?.project_creator?.account_creator?.custom_properties?.find(p => p.key === 'paypal_email')?.value?.[0];
          
          const deliverables = document?.project_creator?.deliverables || [];
          let actual_views_impressions = 0;
          let actual_engagements = 0;
          let hasViewsImpressions = false;
          let hasEngagements = false;
          const liveDates = deliverables
            .map(d => d?.custom_properties?.find(p => p.key === 'live_date')?.value?.[0])
            .filter(Boolean);
          const live_date = liveDates.length ? liveDates.sort()[0] : '';

          for (const deliverable of deliverables) {
            if (deliverable?.custom_properties) {
              const viewsImpressions = deliverable.custom_properties.find(p => p.key === 'actual_views_impressions');
              if (viewsImpressions?.value?.[0]) {
                const parsed = parseFloat(viewsImpressions.value[0]);
                if (!isNaN(parsed)) {
                  actual_views_impressions += parsed;
                  hasViewsImpressions = true;
                }
              }

              const engagements = deliverable.custom_properties.find(p => p.key === 'actual_engagements');
              if (engagements?.value?.[0]) {
                const parsed = parseFloat(engagements.value[0]);
                if (!isNaN(parsed)) {
                  actual_engagements += parsed;
                  hasEngagements = true;
                }
              }
            }
          }
          actual_views_impressions = hasViewsImpressions ? actual_views_impressions : null;
          actual_engagements = hasEngagements ? actual_engagements : null;

          return {
            id: index + 1,
            customProperties: document?.custom_properties,
            document_id: document?.id ? Number(document.id) : null,
            creator_account_id: document?.project_creator_id ? Number(document.project_creator_id) : null,
            label: {
              display_value: document?.label ?? '',
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'label',
              value_type: 'string',
              args: {
                id: Number(document?.id),
              },
            },
            avatar: {
              display_value: document?.project_creator?.account_creator?.avatar ?? '',
            },
            creator: {
              display_value: document?.project_creator?.account_creator ?? '',
            },
            username: {
              display_value: document?.project_creator?.account_creator?.username ?? '',
            },
            first_name: {
              display_value: document?.project_creator?.account_creator?.first_name ?? '',
            },
            last_name: {
              display_value: document?.project_creator?.account_creator?.last_name ?? '',
            },
            file: {
              display_value: {
                id: document?.id ?? null,
                path: document?.path ?? null,
                thumbnail: document?.thumbnail ?? null,
                label: document?.label ?? null,
              },
            },
            type: {
              display_value: document?.type ?? '',
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'type',
              value_type: 'string',
              args: {
                id: Number(document?.id),
              },
            },
            initial_quote: {
              display_value: document?.project_creator?.contract?.contract_value ?? '',
            },
            creator_kill_fee: {
              display_value: document?.project_creator?.kill_fee ? parseFloat(document?.project_creator?.kill_fee) : '',
            },
            invoice_amount: {
              display_value: invoice_amount ? (() => {
                const parsed = parseFloat(invoice_amount);
                return !isNaN(parsed) ? parsed : '';
              })() : '',
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'invoice_amount',
              value_type: 'string',
              custom_property: true,
              originalKey: 'invoice_amount',
              args: {
                id: Number(document?.id),
              },
            },
            currency_custom_property: {
              display_value: currency,
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'currency_custom_property',
              value_type: 'string',
              custom_property: true,
              originalKey: 'Currency',
              args: {
                id: Number(document?.id),
              },
            },
            pay_approved_date: {
              display_value: pay_approved_date ? pay_approved_date : '',
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'pay_approved_date',
              value_type: 'string',
              custom_property: true,
              originalKey: 'pay_approved_date',
              args: {
                id: Number(document?.id),
              },
            },
            approver_name: {
              display_value: approver_name ?? '',
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'approver_name',
              value_type: 'string',
              custom_property: true,
              originalKey: 'approver_name',
              args: {
                id: Number(document?.id),
              },
            },
            paid_date: {
              display_value: paid_date ? paid_date : '',
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'paid_date',
              value_type: 'string',
              custom_property: true,
              originalKey: 'paid_date',
              args: {
                id: Number(document?.id),
              },
            },
            pay_status: {
              display_value: (() => {
                // Map backend DB values (numeric) or GraphQL values (string) to display labels
                if (pay_status === null || pay_status === undefined || pay_status === '') {
                  return 'Pending Approval';
                } else if (pay_status === '0' || pay_status === 'Ready_to_Pay') {
                  return 'Ready to Pay';
                } else if (pay_status === '1' || pay_status === 'Processing') {
                  return 'Processing';
                } else if (pay_status === '2' || pay_status === 'Paid') {
                  return 'Paid';
                } else {
                  // Default to 'Pending Approval' for any unmapped value
                  return 'Pending Approval';
                }
              })(),
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'pay_status',
              value_type: 'string',
              custom_property: true,
              originalKey: 'pay_status',
              args: {
                id: Number(document?.id),
              },
            },
            payment_method: {
              display_value: payment_method ? payment_method : payment_method_src ?? '',
              endpoint: '/graphql/project',
              query: UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION,
              key: 'payment_method',
              value_type: 'string',
              custom_property: true,
              originalKey: 'payment_method',
              args: {
                id: Number(document?.id),
              },
            },
            actual_views_impressions: {
              display_value: actual_views_impressions !== null && actual_views_impressions !== undefined ? actual_views_impressions : '',
            },
            live_date: {
              display_value: live_date ?? '',
            },
            actual_engagements: {
              display_value: actual_engagements !== null && actual_engagements !== undefined ? actual_engagements : '',
            },
            remaining_balance: {
              display_value: remainingBalance !== '' && remainingBalance !== null && remainingBalance !== undefined && !isNaN(remainingBalance)
                ? Math.round(parseFloat(String(remainingBalance)) * 100) / 100
                : '',
            },
            paypal_email_custom_property: {
              display_value: paypal_email ? paypal_email : '',
            },
            creator_contract_status: {
              display_value: document?.project_creator?.contract?.status ?? '',
            },
            creator_contract_notes: {
              display_value: document?.project_creator?.contract?.notes ?? '',
            },
            creator_youtube_reach: {
              display_value:
                document?.project_creator?.youtube_reach && document?.project_creator?.youtube_reach !== 0 && document?.project_creator?.youtube_reach !== '0'
                  ? document?.project_creator?.youtube_reach
                  : '',
            },
            creator_instagram_reach: {
              display_value:
                document?.project_creator?.instagram_reach && document?.project_creator?.instagram_reach !== 0 && document?.project_creator?.instagram_reach !== '0'
                  ? document?.project_creator?.instagram_reach
                  : '',
            },
            creator_tiktok_reach: {
              display_value:
                document?.project_creator?.tiktok_reach && document?.project_creator?.tiktok_reach !== 0 && document?.project_creator?.tiktok_reach !== '0'
                  ? document?.project_creator?.tiktok_reach
                  : '',
            },
            creator_instagram_impressions: {
              display_value:
                document?.project_creator?.instagram_impressions && document?.project_creator?.instagram_impressions !== 0 && document?.project_creator?.instagram_impressions !== '0'
                  ? document?.project_creator?.instagram_impressions
                  : '',
            },
            creator_tiktok_impressions: {
              display_value:
                document?.project_creator?.tiktok_impressions && document?.project_creator?.tiktok_impressions !== 0 && document?.project_creator?.tiktok_impressions !== '0'
                  ? document?.project_creator?.tiktok_impressions
                  : '',
            },
            creator_youtube_impressions: {
              display_value:
                document?.project_creator?.youtube_impressions && document?.project_creator?.youtube_impressions !== 0 && document?.project_creator?.youtube_impressions !== '0'
                  ? document?.project_creator?.youtube_impressions
                  : '',
            },
            creator_instagram_engagement_rate: {
              display_value:
                document?.project_creator?.instagram_engagement_rate &&
                document?.project_creator?.instagram_engagement_rate !== 0 &&
                document?.project_creator?.instagram_engagement_rate !== '0'
                  ? document?.project_creator?.instagram_engagement_rate
                  : '',
            },
            creator_tiktok_engagement_rate: {
              display_value:
                document?.project_creator?.tiktok_engagement_rate && document?.project_creator?.tiktok_engagement_rate !== 0 && document?.project_creator?.tiktok_engagement_rate !== '0'
                  ? document?.project_creator?.tiktok_engagement_rate
                  : '',
            },
            creator_youtube_engagement_rate: {
              display_value:
                document?.project_creator?.youtube_engagement_rate && document?.project_creator?.youtube_engagement_rate !== 0 && document?.project_creator?.youtube_engagement_rate !== '0'
                  ? document?.project_creator?.youtube_engagement_rate
                  : '',
            },
            ...customPropertiesFields,
            ...customEntityMappingsFields,
          };
        });
      } else {
        return [];
      }
    },

    async updateOrCreateDocumentFromGridCell({ rowData, apiBuilder, newValue }) {
      console.log('updateOrCreateDocumentFromGridCell', rowData, apiBuilder, newValue);
      const documentId = rowData?.document_id;
      const projectCreatorId = rowData?.creator_account_id;
      const projectId = useProjectStore().currentProjectID;
      const endpoint = apiBuilder.endpoint;
      const key = apiBuilder.key;
      const originalKey = apiBuilder.originalKey;
      const custom_property = apiBuilder.custom_property;
      
      
      try {
        useProjectStore().isGridUpdating = true;
        if (!documentId) {
          // CREATE
          const args = {
            project_id: Number(projectId),
            project_creator_id: Number(projectCreatorId),
            label: key === 'label' ? newValue : `Document for ${rowData.username.display_value}`,
            [key]: newValue,
          };
          // If custom_property, handle accordingly
          if (custom_property) {
            args.custom_properties = [{ key: originalKey, value: [newValue] }];
          }
          const res = await API.sendQuery(endpoint, CREATE_PROJECT_CREATOR_DOCUMENT_MUTATION, args);
          const newDoc = res.data.data.createProjectCreatorDocument;
          if (newDoc && newDoc.id) {
            // Update the rowData with the new document_id and any returned fields
            rowData.document_id = newDoc.id;
            if (newDoc.filename) rowData.file_name.display_value = newDoc.filename;
            if (newDoc.path) rowData.file.display_value.path = newDoc.path;
            // Update the documents array in the store if needed
            // Find the index in this.data and update
            const idx = this.data.findIndex((d) => d.creator_account_id === projectCreatorId);
            if (idx !== -1) {
              this.data[idx].document_id = newDoc.id;
              if (newDoc.filename) this.data[idx].file_name.display_value = newDoc.filename;
              if (newDoc.path) this.data[idx].file.display_value.path = newDoc.path;
            }
            // Optionally update this.documents as well
            this.setDocuments([...(this.documents || []), newDoc]);
          }
        } else {
          // UPDATE
          const args = {
            id: Number(documentId),
            [key]: newValue,
          };
          if (custom_property) {
            args.custom_properties = [{ key: originalKey, value: [newValue] }];
          }
          await API.sendQuery(endpoint, UPDATE_PROJECT_CREATOR_DOCUMENT_MUTATION, args);
        }
      } catch (error) {
        console.error('Error in updateOrCreateDocumentFromGridCell:', error);
      } finally {
        useProjectStore().isGridUpdating = false;
      }
    },
  },
});
